import {useMemo} from "react";

export interface WekaProduct {
    id: number;
    data: {
        name: string;
        slug: string;
        date_created: string | Date;
        date_modified: string | Date;
        status: 'publish' | 'draft' | string;
        featured: boolean;
        catalog_visibility: 'visible' | 'hidden' | 'catalog' | 'search' | string;
        description: string;
        short_description: string;
        sku: string;
        global_unique_id: string;
        price: string;
        regular_price: string;
        sale_price: string;
        date_on_sale_from: string | Date | null;
        date_on_sale_to: string | Date | null;
        total_sales: number;
        tax_status: 'taxable' | 'none' | string;
        tax_class: string;
        manage_stock: boolean;
        stock_quantity: number | null;
        stock_status: 'instock' | 'outofstock' | 'onbackorder' | string;
        backorders: 'no' | 'notify' | 'yes' | string;
        low_stock_amount: string | number | null;
        sold_individually: boolean;
        weight: string;
        length: string;
        width: string;
        height: string;
        upsell_ids: number[];
        cross_sell_ids: number[];
        parent_id: number;
        reviews_allowed: boolean;
        purchase_note: string;
        attributes: any[];
        default_attributes: any[];
        menu_order: number;
        post_password: string;
        virtual: boolean;
        downloadable: boolean;
        category_ids: number[];
        tag_ids: number[];
        shipping_class_id: number;
        downloads: any[];
        image_id: string | number;
        gallery_image_ids: (string | number)[];
        download_limit: number;
        download_expiry: number;
        rating_counts: Record<string, number>;
        average_rating: string;
        review_count: number;
        cogs_value: number | null;
        meta_data: {id: number, key: string, value: string | Array<any>}[]
    };
}

export interface UnitConversion {
    factor: number
    type: string,
    from: string,
    to: string
}

export interface PriceEntry {
    quantity: number;
    currency_symbol: string;
    currency_code?: string;
    price: string | number;
    uom: string;
}

export interface Pricing {
    [uom: string]: {
        [quantity: string]: PriceEntry;
    };
}

export interface QuantityBreaks {
    [quantity: string]: number;
}

export interface ProductPricing {
    pricing: Pricing;
    quantity_breaks: QuantityBreaks;
}

export interface BreakpointEntry {
    min_qty: number;
    price: number;
    currency_symbol: string;
    currency_code?: string;
    uom: string;
}

export interface BreakpointLookup {
    [uom: string]: BreakpointEntry[];
}

export const createPricingCalculator = (
    base_price: number,
    unitConversions: UnitConversion[],
    salesPrices: ProductPricing,
    breakpointLookup?: BreakpointLookup,
    quantityBreaksEnabled: boolean = true,
    baseUom: string = 'EA'
) => {
    const resolveBaseUomUnitPrice = (quantity: number): number => {
        const normalizedBaseUom = String(baseUom || 'EA').toUpperCase();

        if (quantityBreaksEnabled) {
            const baseLookupRows = breakpointLookup?.[normalizedBaseUom];
            if (baseLookupRows && baseLookupRows.length > 0) {
                let selected = baseLookupRows[0];
                for (const row of baseLookupRows) {
                    if (quantity >= Number(row.min_qty || 1)) {
                        selected = row;
                        continue;
                    }
                    break;
                }
                return Number(selected.price || base_price);
            }
        }

        if (!quantityBreaksEnabled) {
            return base_price;
        }

        const baseSalesRows = salesPrices?.pricing?.[normalizedBaseUom];
        if (baseSalesRows) {
            let selectedRow: PriceEntry | null = null;
            for (const row of Object.values(baseSalesRows)) {
                const threshold = quantityBreaksEnabled ? Number(row.quantity || 1) : 1;
                if (quantity >= threshold) {
                    selectedRow = row;
                    continue;
                }
                break;
            }
            if (selectedRow !== null) {
                return Number(selectedRow.price || base_price);
            }
        }

        return base_price;
    };

    return (uom: string, quantity: number): number => {
        const normalizedUom = String(uom || '').toUpperCase();

        if (quantityBreaksEnabled) {
            const lookupRows = breakpointLookup?.[normalizedUom];
            if (lookupRows && lookupRows.length > 0) {
                let selected = lookupRows[0];
                for (const row of lookupRows) {
                    if (quantity >= Number(row.min_qty || 1)) {
                        selected = row;
                        continue;
                    }
                    break;
                }

                return Number(selected.price || 0) * quantity;
            }
        }

        if (!quantityBreaksEnabled) {
            const conversionForUom = unitConversions?.find((conversion) =>
                String(conversion.from || '').toUpperCase() === normalizedUom
                && String(conversion.to || '').toUpperCase() === String(baseUom || 'EA').toUpperCase()
            );

            if (conversionForUom) {
                return (Number(conversionForUom.factor || 0) * base_price) * quantity;
            }

            return base_price * quantity;
        }

        const salesPricesForUom = salesPrices?.pricing?.[normalizedUom];
        if (salesPricesForUom) {
            let quantityBreakForQuantity: PriceEntry | null = null;

            for (const priceEntry of Object.values(salesPricesForUom)) {
                const threshold = quantityBreaksEnabled ? priceEntry.quantity : 1;
                if (quantity >= threshold) {
                    quantityBreakForQuantity = priceEntry;
                } else {
                    break;
                }
            }

            if (quantityBreakForQuantity != null) {
                return parseFloat(quantityBreakForQuantity.price) * quantity;
            }
        }

        const conversionForUom = unitConversions?.find((conversion) =>
            String(conversion.from || '').toUpperCase() === normalizedUom
            && String(conversion.to || '').toUpperCase() === String(baseUom || 'EA').toUpperCase()
        );
        if (conversionForUom) {
            const baseUnitPrice = resolveBaseUomUnitPrice(quantity);
            return (Number(conversionForUom.factor || 0) * baseUnitPrice) * quantity;
        }

        return base_price * quantity;
    };
};

export function usePrice(
    base_price: number,
    unitConversions: UnitConversion[],
    salesPrices: ProductPricing,
    breakpointLookup?: BreakpointLookup,
    quantityBreaksEnabled: boolean = true,
    baseUom: string = 'EA'
) {
    const pricing = useMemo(() => createPricingCalculator(
        base_price,
        unitConversions,
        salesPrices,
        breakpointLookup,
        quantityBreaksEnabled,
        baseUom
    ), [base_price, unitConversions, salesPrices, breakpointLookup, quantityBreaksEnabled, baseUom]);

    return { pricing };
}
